import { encodeCode39 } from "./code39.js";
import {
  applyMarginSettingsToFormat,
  resolveBrandTopMm,
  resolveBrandZoneMm,
  resolveContentMarginMm,
  resolveHorizontalOffsetMm,
  shiftLayoutYMm,
} from "./margin-settings.js";
import type { LabelPrintFormat, LabelPrintMarginSettings, ProductLabelData } from "./types.js";
import type { LabelPrintRotation } from "./rotation.js";

/** 203 dpi — standard TSC / 4BARCODE. */
const DOTS_PER_MM = 8;

function escapeTsplText(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function mmToDots(mm: number): number {
  return Math.round(mm * DOTS_PER_MM);
}

/** Marge gauche pour centrer l'étiquette sur un rouleau plus large. */
export function computeRollCenterOffsetMm(format: LabelPrintFormat): number {
  if (!format.rollWidthMm || format.rollWidthMm <= format.widthMm) {
    return 0;
  }
  const fullMargin = (format.rollWidthMm - format.widthMm) / 2;
  const factor = format.rollOffsetFactor ?? 1;
  return Math.round(fullMargin * factor * 10) / 10;
}

/** Largeur passée à SIZE TSPL (rouleau entier si centré, sinon étiquette). */
export function tsplPageWidthMm(format: LabelPrintFormat): number {
  return format.rollWidthMm ?? format.widthMm;
}

function maxNameChars(widthMm: number): number {
  if (widthMm >= 100) return 30;
  if (widthMm >= 75) return 22;
  if (widthMm >= 60) return 16;
  if (widthMm >= 58) return 18;
  return 14;
}

function tsplRotation(rotation: LabelPrintRotation): number {
  return rotation;
}

interface LayoutLine {
  yMm: number;
  font: string;
  xMul: number;
  yMul: number;
  text: string;
}

function isCompactFormat(format: LabelPrintFormat): boolean {
  return format.heightMm <= 50;
}

/** Étiquette étroite (≤40 mm haut) — nom + code-barres. */
function buildCompactLayout(
  format: LabelPrintFormat,
  label: ProductLabelData,
  margins?: LabelPrintMarginSettings,
): {
  marginMm: number;
  lines: LayoutLine[];
  barcodeYMm: number;
  barcodeHeightMm: number;
  human: string;
} {
  const tallCompact = format.heightMm > 32;
  const extraTall = format.heightMm >= 45;
  const nearSquare = format.heightMm >= 48;
  const includeBrand = Boolean(format.rollWidthMm) && format.heightMm >= 40;
  const brandZoneMm = includeBrand ? resolveBrandZoneMm(margins, 15) : 0;
  const marginMm = includeBrand
    ? resolveContentMarginMm(margins, 1)
    : tallCompact
      ? 2
      : 1.5;
  let yMm: number;
  const lines: LayoutLine[] = [];
  const human =
    encodeCode39(label.barcode).replace(/^\*|\*$/g, "") || label.barcode.trim();
  const name = truncate(label.productName.trim(), maxNameChars(format.widthMm));
  const wideCompact = format.widthMm >= 58;

  if (includeBrand) {
    lines.push({
      yMm: resolveBrandTopMm(margins, 7),
      font: "2",
      xMul: 1,
      yMul: 1,
      text: "ITaaS Hospitol",
    });
    yMm = brandZoneMm;
  } else {
    yMm = marginMm;
  }

  lines.push({
    yMm,
    font: includeBrand ? "3" : wideCompact ? "4" : "3",
    xMul: wideCompact && !includeBrand && format.widthMm >= 70 && tallCompact ? 2 : 1,
    yMul: 1,
    text: name,
  });
  yMm += includeBrand
    ? 4
    : nearSquare
      ? 8.5
      : extraTall
        ? 8
        : tallCompact
          ? wideCompact
            ? 7
            : 5
          : wideCompact
            ? 5
            : 4;

  const reserveBottom = includeBrand
    ? 5
    : nearSquare
      ? 7.5
      : extraTall
        ? 7
        : tallCompact
          ? 6
          : 5;
  const barcodeHeightMm = Math.min(
    includeBrand
      ? 11
      : nearSquare
        ? 20
        : extraTall
          ? 19
          : tallCompact
            ? 17
            : wideCompact
              ? 14
              : 16,
    Math.max(includeBrand ? 10 : 11, format.heightMm - yMm - reserveBottom),
  );
  const barcodeYMm = yMm;
  yMm += barcodeHeightMm + (nearSquare ? 3 : extraTall ? 2.5 : tallCompact ? 2 : 1.5);

  lines.push({
    yMm,
    font: includeBrand ? "3" : wideCompact ? "4" : "3",
    xMul: 1,
    yMul: 1,
    text: human,
  });

  return { marginMm, lines, barcodeYMm, barcodeHeightMm, human };
}

/**
 * Mise en page verticale en mm — évite les chevauchements (ancien gabarit : réf. à y=148
 * sous le code-barres qui commençait à y=96, hauteur 64).
 */
function buildVerticalLayout(
  format: LabelPrintFormat,
  label: ProductLabelData,
  margins?: LabelPrintMarginSettings,
): {
  marginMm: number;
  lines: LayoutLine[];
  barcodeYMm: number;
  barcodeHeightMm: number;
  human: string;
} {
  if (isCompactFormat(format)) {
    return buildCompactLayout(format, label, margins);
  }

  const marginMm = margins?.contentMarginMm ?? 3;
  let yMm = margins?.marginTopMm ?? marginMm;
  const lines: LayoutLine[] = [];
  const human =
    encodeCode39(label.barcode).replace(/^\*|\*$/g, "") || label.barcode.trim();
  const name = truncate(label.productName.trim(), maxNameChars(format.widthMm));

  const largeLabel = format.heightMm >= 100;
  const wideLabel = format.widthMm >= 76;

  const fontSmall = "3";
  const fontMedium = "4";
  const nameMul = wideLabel ? 2 : 1;
  const codeMul = wideLabel ? 2 : 1;

  lines.push({
    yMm,
    font: fontSmall,
    xMul: 1,
    yMul: 1,
    text: "ITaaS Hospitol",
  });
  yMm += 5;

  lines.push({
    yMm,
    font: fontMedium,
    xMul: nameMul,
    yMul: nameMul,
    text: name,
  });
  yMm += largeLabel ? 10 : 8;

  if (label.sku && label.sku !== label.barcode) {
    lines.push({
      yMm,
      font: fontSmall,
      xMul: 1,
      yMul: 1,
      text: `Réf. ${truncate(label.sku, maxNameChars(format.widthMm))}`,
    });
    yMm += 5;
  }

  const barcodeHeightMm = Math.min(
    20,
    Math.max(14, format.heightMm * 0.17),
  );
  const barcodeYMm = yMm;
  yMm += barcodeHeightMm + 3;

  lines.push({
    yMm,
    font: fontMedium,
    xMul: codeMul,
    yMul: 1,
    text: human,
  });
  yMm += 7;

  if (label.quantity != null && label.quantity > 0) {
    lines.push({
      yMm,
      font: fontSmall,
      xMul: 1,
      yMul: 1,
      text: `Qté: ${label.quantity}${label.unit ? ` ${label.unit}` : ""}`,
    });
  }

  return { marginMm, lines, barcodeYMm, barcodeHeightMm, human };
}

/** Commandes TSPL — positions en mm converties en points, rotation par élément. */
export function buildTsplLabelDocument(
  label: ProductLabelData,
  format: LabelPrintFormat,
  rotation: LabelPrintRotation = 0,
  margins?: LabelPrintMarginSettings,
): string {
  const effectiveFormat = applyMarginSettingsToFormat(format, margins);
  let { marginMm, lines, barcodeYMm, barcodeHeightMm, human } = buildVerticalLayout(
    effectiveFormat,
    label,
    margins,
  );

  const extraTopMm =
    margins?.marginTopMm != null && !isCompactFormat(effectiveFormat)
      ? Math.max(0, margins.marginTopMm - marginMm)
      : 0;
  if (extraTopMm > 0) {
    const shifted = shiftLayoutYMm(lines, barcodeYMm, extraTopMm);
    lines = shifted.lines;
    barcodeYMm = shifted.barcodeYMm;
  }

  const rot = tsplRotation(rotation);
  const centerOffsetMm = computeRollCenterOffsetMm(effectiveFormat);
  const xMm = resolveHorizontalOffsetMm(
    effectiveFormat,
    marginMm,
    margins,
    centerOffsetMm,
  );
  const xDots = mmToDots(xMm);
  const pageWidthMm = tsplPageWidthMm(effectiveFormat);
  const barcodeHeightDots = mmToDots(barcodeHeightMm);
  const barNarrow = isCompactFormat(effectiveFormat) ? (effectiveFormat.widthMm >= 58 ? 3 : 2) : 3;
  const barWide = isCompactFormat(effectiveFormat) ? (effectiveFormat.widthMm >= 58 ? 5 : 4) : 6;

  let body = "";
  for (const line of lines) {
    body +=
      `TEXT ${xDots},${mmToDots(line.yMm)},"${line.font}",${rot},${line.xMul},${line.yMul},"${escapeTsplText(line.text)}"\r\n`;
  }

  return (
    `SIZE ${pageWidthMm} mm, ${effectiveFormat.heightMm} mm\r\n` +
    `GAP 2 mm, 0 mm\r\n` +
    `DIRECTION 0\r\n` +
    `REFERENCE 0,0\r\n` +
    `OFFSET 0 mm\r\n` +
    `SET TEAR ON\r\n` +
    `CLS\r\n` +
    body +
    `BARCODE ${xDots},${mmToDots(barcodeYMm)},"39",${barcodeHeightDots},0,${rot},${barNarrow},${barWide},"${escapeTsplText(human)}"\r\n` +
    `PRINT 1,1\r\n`
  );
}
